import { useInterviewStore } from '@/stores/useInterviewStore'
import { FileText, Notebook, Plus } from 'lucide-react'
import { useState, useEffect, useRef } from 'react'
import { BrainstormDocumentContent } from './BrainstormDocumentContent'
import { FoolscapDocumentContent } from './FoolscapDocumentContent'
import { ScratchpadDocumentContent } from './ScratchpadDocumentContent'

type TabType = 'brainstorm' | 'foolscap' | 'scratchpad'

export function DocumentPanel() {
  const {
    buildMode,
    sessionId,
    foolscapContent,
    foolscapDocumentPending,
    scratchpadContent,
    brainstormKeyThemes,
    brainstormGaps,
  } = useInterviewStore()

  const [activeTab, setActiveTab] = useState<TabType>('brainstorm')
  // Track if user manually switched tabs - don't auto-switch back while streaming
  const userSwitchedTabRef = useRef(false)
  const prevBuildModeRef = useRef(buildMode)

  // Handle manual tab clicks - mark as user-initiated
  const handleTabClick = (tab: TabType) => {
    userSwitchedTabRef.current = true
    setActiveTab(tab)
  }

  // Reset to brainstorm tab when session changes (new session)
  useEffect(() => {
    setActiveTab('brainstorm')
    userSwitchedTabRef.current = false
  }, [sessionId])

  // Reset to brainstorm tab when foolscap content disappears (unless still in foolscap mode)
  useEffect(() => {
    if (!foolscapContent && activeTab === 'foolscap' && buildMode !== 'foolscap') {
      setActiveTab('brainstorm')
      userSwitchedTabRef.current = false
    }
  }, [foolscapContent, activeTab, buildMode])

  // Auto-switch to foolscap tab ONCE when entering foolscap mode
  // Don't auto-switch if user manually moved to another tab
  useEffect(() => {
    const justEnteredFoolscap = buildMode === 'foolscap' && prevBuildModeRef.current !== 'foolscap'
    prevBuildModeRef.current = buildMode

    if (justEnteredFoolscap && !userSwitchedTabRef.current) {
      setActiveTab('foolscap')
    }

    // Reset user switch flag when leaving foolscap mode
    if (buildMode !== 'foolscap') {
      userSwitchedTabRef.current = false
    }
  }, [buildMode])

  // Show Foolscap tab when content exists OR when in foolscap mode (preparing/pending)
  const showFoolscapTab = Boolean(foolscapContent) || buildMode === 'foolscap' || foolscapDocumentPending
  // Show Scratchpad tab when content exists OR when in foolscap mode
  const showScratchpadTab = Boolean(scratchpadContent) || buildMode === 'foolscap'
  const isBrainstormReadOnly = buildMode !== 'brainstorm'

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 border-b border-border">
        {/* Brainstorm tab */}
        <button
          onClick={() => handleTabClick('brainstorm')}
          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
            activeTab === 'brainstorm'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
        >
          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Brainstorm</span>
          {isBrainstormReadOnly && (
            <span className="ml-1 text-xs text-muted-foreground">(read-only)</span>
          )}
        </button>

        {/* Foolscap tab - only show when content exists */}
        {showFoolscapTab && (
          <button
            onClick={() => handleTabClick('foolscap')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
              activeTab === 'foolscap'
                ? 'bg-muted/50 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Foolscap</span>
          </button>
        )}

        {/* Scratchpad tab - only show when content exists or in foolscap mode */}
        {showScratchpadTab && (
          <button
            onClick={() => handleTabClick('scratchpad')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
              activeTab === 'scratchpad'
                ? 'bg-muted/50 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            <Notebook className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span className="font-light">Scratchpad</span>
          </button>
        )}

        {/* Add tab button (placeholder for future) */}
        <button
          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded transition-colors"
          title="Add document"
        >
          <Plus className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Metadata bar (themes, gaps) - only for brainstorm */}
      {activeTab === 'brainstorm' && (brainstormKeyThemes.length > 0 || brainstormGaps.length > 0) && (
        <div className="flex-shrink-0 px-4 py-2 text-xs border-b border-border">
          {brainstormKeyThemes.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {brainstormKeyThemes.map((theme, i) => (
                <span key={i} className="px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                  {theme}
                </span>
              ))}
            </div>
          )}
          {Array.isArray(brainstormGaps) && brainstormGaps.length > 0 && (
            <div className="mt-1.5 text-muted-foreground">
              <span className="font-medium">Gaps:</span> {brainstormGaps.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Document content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'brainstorm' && (
          <BrainstormDocumentContent editable={!isBrainstormReadOnly} />
        )}
        {activeTab === 'foolscap' && (
          <FoolscapDocumentContent editable={buildMode === 'foolscap'} />
        )}
        {activeTab === 'scratchpad' && (
          <ScratchpadDocumentContent />
        )}
      </div>
    </div>
  )
}
